// Live view of a client's Drive folder (6 - Client Folder / <G-number>) via the
// list-client-folder edge function. This is what is actually on file — whether
// it came through CAERA, SignWell, or was dropped in by hand.

import { supabase } from './supabase';

// Returns { folderId, folders: [{ id, name, webViewLink, files, subfolders }], files }
// — subfolders nest to any depth, each { id, name, webViewLink, files,
// subfolders, folderCount } (folderCount: folders past the depth read).
//
// The function takes the folder from the client row; a request never names one.
//
// A page can load a moment before the session is ready, and that one call goes
// out on the anon key, which the function refuses. It happened about once in
// 400 calls in the week before the check went in, so a 401 is tried once more
// before it is shown to anyone as an error.
const SESSION_RETRY_MS = 1500;

export async function listClientFolder(clientCode) {
  const list = () => supabase.functions.invoke('list-client-folder', { body: { clientCode } });
  let { data, error } = await list();
  if (error?.context?.status === 401) {
    await new Promise((resolve) => setTimeout(resolve, SESSION_RETRY_MS));
    ({ data, error } = await list());
  }
  if (error) throw new Error(await functionErrorMessage(error));
  if (!data?.success) throw new Error(data?.error || 'Could not read the Drive folder');
  return data;
}

// On a non-2xx response supabase-js gives a generic "Edge Function returned a
// non-2xx status code" and leaves the function's own { error } body on
// error.context. Surface that body — it names the actual Drive failure.
async function functionErrorMessage(error) {
  try {
    const body = await error?.context?.json?.();
    if (body?.error) return body.error;
  } catch { /* body wasn't JSON — fall through to the generic message */ }
  return error?.message || 'Could not read the Drive folder';
}

// Find a standard bucket (e.g. "Signing Packets") in a listing.
export function findBucket(listing, name) {
  return (listing?.folders || []).find((f) => f.name.toLowerCase() === String(name).toLowerCase()) || null;
}

// Every file inside a bucket, at any depth, flattened with a `path`
// ("initial April 2026 / Change Request") so a file picker can show where each
// one sits. The Files tab shows the folders themselves (FolderTree) instead.
export function flattenBucket(bucket) {
  if (!bucket) return [];
  const out = (bucket.files || []).map((f) => ({ ...f, path: '' }));
  const walk = (sf, path) => {
    const here = path ? `${path} / ${sf.name}` : sf.name;
    (sf.files || []).forEach((f) => out.push({ ...f, path: here }));
    (sf.subfolders || []).forEach((x) => walk(x, here));
    if (sf.folderCount) out.push({ id: sf.id, name: `${sf.name} (${sf.folderCount} more folder${sf.folderCount === 1 ? '' : 's'} inside)`, mimeType: 'application/vnd.google-apps.folder', webViewLink: sf.webViewLink, modifiedTime: sf.modifiedTime, path });
  };
  (bucket.subfolders || []).forEach((sf) => walk(sf, ''));
  return out.sort((a, b) => String(b.modifiedTime).localeCompare(String(a.modifiedTime)));
}

// Files in a folder and everything under it — for counts and "latest".
export function filesUnder(folder) {
  if (!folder) return [];
  return [...(folder.files || []), ...(folder.subfolders || []).flatMap(filesUnder)];
}

export function fileKind(mimeType = '', name = '') {
  if (mimeType.includes('folder')) return 'Folder';
  if (mimeType.includes('pdf')) return 'PDF';
  if (mimeType.includes('wordprocessingml') || mimeType.includes('google-apps.document')) return 'Word';
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return 'Sheet';
  if (mimeType.startsWith('image/')) return 'Image';
  const ext = name.split('.').pop();
  return ext && ext.length <= 4 ? ext.toUpperCase() : 'File';
}

export function fmtSize(bytes) {
  const n = Number(bytes);
  if (!n) return '';
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
